import { useState } from 'react'

import { useGetProjection } from './api/generated/fluence'
import { AccountCard } from './components/AccountCard'
import { BalanceTimeline } from './components/BalanceTimeline'
import { AccountEditor } from './components/AccountEditor'
import { AnchorEditor } from './components/AnchorEditor'
import { CardEditor } from './components/CardEditor'
import { CashFlowEditor } from './components/CashFlowEditor'
import { GettingStarted } from './components/GettingStarted'
import { LoanEditor } from './components/LoanEditor'
import { Reconciliation } from './components/Reconciliation'
import { SignedInAs } from './components/SignedInAs'
import { FluenceAssistant } from './assistant/FluenceAssistant'
import { formatDate, formatMoney } from './lib/money'

const HORIZON_OPTIONS = [6, 12, 24] as const

export default function App() {
  // The runtime wraps the whole app, not just onboarding: Copilot features read and act on
  // the main UI, and they need the provider above the tree they touch.
  return (
    <FluenceAssistant>
      <AppShell />
    </FluenceAssistant>
  )
}

function AppShell() {
  const [horizonMonths, setHorizonMonths] = useState<number>(24)
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const { data, isPending, isError } = useGetProjection({ horizonMonths })

  // The contract documents a 400 for an out-of-range horizon, so the generated response is
  // a union and `data` is only a Projection on 200. Narrowing on status here is the
  // generated client telling the truth rather than an inconvenience to work around.
  const projection = data?.status === 200 ? data.data : undefined
  const badRequest = data?.status === 400

  const selected =
    projection?.accounts.find((a) => a.accountId === selectedId) ?? projection?.accounts[0]

  return (
    <main className="shell">
      <header className="masthead">
        <div>
          <h1>Fluence</h1>
          <p className="tagline">
            {projection
              ? `${formatDate(projection.asOf)} — ${formatDate(projection.horizonEnd)}`
              : 'Personal liquidity planning'}
          </p>
        </div>
        <div className="masthead__right">
          <SignedInAs />
          <div className="horizon-switch" role="group" aria-label="Projection horizon">
          {HORIZON_OPTIONS.map((months) => (
            <button
              key={months}
              type="button"
              className={months === horizonMonths ? 'is-active' : ''}
              onClick={() => setHorizonMonths(months)}
            >
              {months}m
            </button>
            ))}
          </div>
        </div>
      </header>

      {isPending && <p className="notice">Projecting…</p>}

      {badRequest && (
        <p className="notice is-error">
          The backend rejected that horizon. Pick 6, 12 or 24 months.
        </p>
      )}

      {isError && (
        <p className="notice is-error">
          No response from the backend. Start it with{' '}
          <code>mvn -pl backend/fluence-app spring-boot:run</code>.
        </p>
      )}

      {projection && projection.accounts.length === 0 && <GettingStarted />}

      {projection && projection.accounts.length > 0 && (
        <>
          <section
            className={`verdict ${projection.solvent ? 'is-good' : 'is-bad'}`}
            aria-live="polite"
          >
            <span className="verdict-mark" aria-hidden="true">
              {projection.solvent ? '✓' : '!'}
            </span>
            <div>
              <h2>
                {projection.solvent
                  ? 'Every account stays above zero'
                  : 'An account goes negative'}
              </h2>
              <p>
                {projection.solvent
                  ? `Across the next ${horizonMonths} months, given everything currently modelled.`
                  : firstProblem(projection.accounts)}
              </p>
            </div>
          </section>

          <section className="accounts">
            {projection.accounts.map((ledger) => (
              <AccountCard
                key={ledger.accountId}
                ledger={ledger}
                selected={ledger.accountId === selected?.accountId}
                onSelect={() => setSelectedId(ledger.accountId)}
              />
            ))}
          </section>

          {selected && (
            <section className="panel">
              <div className="panel-head">
                <h2>{selected.name}</h2>
                <span className="panel-note">
                  hover the chart to inspect any day · buffer{' '}
                  {formatMoney(selected.bufferTargetMinor, selected.currency)}
                </span>
              </div>
              <BalanceTimeline ledger={selected} />
            </section>
          )}

          <AccountEditor />

          <AnchorEditor accounts={projection.accounts} />

          <Reconciliation />

          <CashFlowEditor accounts={projection.accounts} />

          <CardEditor accounts={projection.accounts} />

          <LoanEditor accounts={projection.accounts} />

          <section className="panel">
            <div className="panel-head">
              <h2>Total liquidity</h2>
              <span className="panel-note">
                {projection.portfolio.currency} · aggregate does not imply solvency
              </span>
            </div>
            <dl className="facts">
              <div className="fact">
                <dt>Lowest total</dt>
                <dd>
                  {formatMoney(
                    projection.portfolio.minimumTotalMinor,
                    projection.portfolio.currency,
                  )}
                </dd>
                <span className="figure-note">
                  {formatDate(projection.portfolio.minimumTotalDate)}
                </span>
              </div>
              <div className="fact">
                <dt>At horizon end</dt>
                <dd>
                  {formatMoney(
                    projection.portfolio.closingTotalMinor,
                    projection.portfolio.currency,
                  )}
                </dd>
              </div>
            </dl>
          </section>
        </>
      )}
    </main>
  )
}

/** The headline sentence when something is wrong: which account, when, how bad. */
function firstProblem(accounts: { name: string; breaches: { type: string; date: string }[] }[]) {
  for (const account of accounts) {
    const zero = account.breaches.find((b) => b.type === 'ZERO')
    if (zero) {
      return `${account.name} falls below zero on ${formatDate(zero.date)}.`
    }
  }
  return 'See the accounts below.'
}
